// ============================================
// FILE: src/features/lockers/components/LockerEdit.tsx
// VERSIÓN CORREGIDA - Con mapeo de datos correctos
// ============================================

import React, { useState, useMemo } from 'react';
import {
  Edit,
  SimpleForm,
  TextInput,
  ReferenceInput,
  SelectInput,
  required,
  useTranslate,
  useNotify,
  useRedirect,
  useRecordContext,
  LoadingIndicator,
  number,
  minValue,
} from 'react-admin';
import {
  Box,
  Grid,
  Typography,
  Chip,
  Alert,
} from '@mui/material';
import { 
  Edit as EditIcon,
} from '@mui/icons-material';
import { MyToolbar } from '../../../components/forms/MyToolbar';
import { MyFormContainer } from '../../../components/forms/MyFormContainer';
import { 
  CenteredFormLayout,
  MyFormSection 
} from '../../../components/forms/MyFormLayout';
import BaseDeleteDialog from '../../../components/common/BaseDeleteDialog';
import { LockerStatus, Locker } from '../types';
import { getLockerStatusColor, getLockerStatusLabel } from '../types';

// Validation hooks
const useLockerValidation = () => {
  const translate = useTranslate();
  
  const validateDisplayNumber = (value: string) => {
    if (!value) return undefined;
    
    const trimmed = value.trim();
    if (trimmed.length < 1) {
      return translate('validation.minLength', { min: 1 });
    }
    if (trimmed.length > 50) {
      return translate('validation.maxLength', { max: 50 });
    }
    
    return undefined;
  };
  
  const validateHardwareId = (value: number) => {
    if (!value) return undefined;
    
    if (value < 1) {
      return translate('validation.positive');
    }
    
    return undefined;
  };
  
  return {
    validateDisplayNumber,
    validateHardwareId,
  };
};

// Title component
const LockerEditTitle: React.FC = () => {
  const record = useRecordContext<Locker>();
  const translate = useTranslate();
  
  if (!record) return <LoadingIndicator />;
  
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
      <span>
        {translate('resources.lockers.titles.edit')}: {record.displayNumber}
      </span>
      <Chip 
        label={getLockerStatusLabel(record.status)}
        color={getLockerStatusColor(record.status)}
        size="small"
      />
    </Box>
  );
};

// Main Edit Component
const LockerEdit: React.FC = (props) => {
  const translate = useTranslate();
  const notify = useNotify();
  const redirect = useRedirect();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  
  // ✅ TRANSFORM para el SUBMIT (datos que van al backend)
  const transform = (data: any) => {
    return {
      displayNumber: data.displayNumber?.trim(),
      hardwareId: parseInt(data.hardwareId, 10),
      hotelId: data.hotelId,
      zoneId: data.zoneId,
      lockerTypeId: data.lockerTypeId,
      lockerControllerId: data.lockerControllerId || null,
      notes: data.notes?.trim() || null,
    };
  };
  
  const handleSuccess = (data: any) => {
    notify(
      translate('resources.lockers.notifications.updated', { 
        displayNumber: data?.displayNumber 
      }), 
      { type: 'success' }
    );
    redirect('list', 'lockers');
  };
  
  const handleError = (error: any) => {
    console.error('Error updating locker:', error);
    notify(
      error?.body?.message || error?.message || translate('resources.lockers.errors.update'), 
      { type: 'error' }
    );
  };
  
  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
  };
  
  return (
    <Edit
      {...props}
      title={<LockerEditTitle />}
      transform={transform}
      mutationOptions={{ 
        onSuccess: handleSuccess, 
        onError: handleError 
      }}
      redirect={false}
      mutationMode="pessimistic"
    >
      <LockerEditForm onDelete={handleDeleteClick} />
      
      <LockerDeleteDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      />
    </Edit>
  );
};

// Form Component
const LockerEditForm: React.FC<{ onDelete?: () => void }> = ({ onDelete }) => {
  const originalRecord = useRecordContext<any>();
  const translate = useTranslate();
  const redirect = useRedirect();
  const { validateDisplayNumber, validateHardwareId } = useLockerValidation();
  
  // ✅ TRANSFORMAR los datos de la API al formato del formulario
  const record = useMemo(() => {
    if (!originalRecord) return null;
    
    return {
      ...originalRecord,
      // Mapear objetos anidados a IDs planos
      hotelId: originalRecord.hotel?.id || originalRecord.hotelId,
      zoneId: originalRecord.zone?.id || originalRecord.zoneId,
      lockerTypeId: originalRecord.lockerType?.id || originalRecord.lockerTypeId,
      lockerControllerId: originalRecord.controller?.id || originalRecord.lockerControllerId,
    };
  }, [originalRecord]);
  
  if (!record) return <LoadingIndicator />;
  
  const handleCancel = () => redirect('list', 'lockers');
  
  // Show warning if locker is occupied
  const isOccupied = record?.status === LockerStatus.Occupied;
  
  // ✅ Obtener el hotelId correcto para los filtros
  const hotelId = originalRecord?.hotel?.id || originalRecord?.hotelId;
  
  return (
    <MyFormContainer maxWidth="lg">
      <SimpleForm
        record={record} // ✅ Pasar el record transformado al formulario
        toolbar={
          <MyToolbar 
            onCancel={handleCancel} 
            resource="lockers" 
            showDelete={!isOccupied}
            onDelete={onDelete}
          />
        }
      >
        <CenteredFormLayout>
          {/* Warning for occupied lockers */}
          {isOccupied && (
            <Alert severity="warning" sx={{ mb: 3 }}>
              <Typography variant="body2">
                {translate('resources.lockers.warnings.occupied_edit')}
              </Typography>
            </Alert>
          )}
          
          {/* Basic Information Section */}
          <MyFormSection
            variant='outlined'
            title={translate('resources.lockers.sections.basic_info')}
            subtitle={translate('resources.lockers.sections.basic_info_subtitle')}
            icon={<EditIcon />}
          >
            <Grid container spacing={3}>
              {/* Primera fila: Display Number y Hardware ID */}
              <Grid item xs={12} md={6}>
                <TextInput
                  source="displayNumber"
                  label={translate('resources.lockers.fields.displayNumber')}
                  validate={[required(), validateDisplayNumber]}
                  fullWidth
                  variant="outlined"
                  helperText={translate('resources.lockers.help.displayNumber')}
                  disabled={isOccupied}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextInput
                  source="hardwareId"
                  label={translate('resources.lockers.fields.hardwareId')}
                  validate={[required(), number(), minValue(1), validateHardwareId]}
                  fullWidth
                  variant="outlined"
                  helperText={translate('resources.lockers.help.hardwareId')}
                  disabled={isOccupied}
                />
              </Grid>
              
              {/* Segunda fila: Zone */}
              <Grid item xs={12}>
                <ReferenceInput
                  source="zoneId"
                  reference="zones"
                  label={translate('resources.lockers.fields.zone')}
                  filter={{ hotelId: hotelId }} // ✅ Usar el hotelId correcto
                  sort={{ field: 'name', order: 'ASC' }}
                >
                  <SelectInput 
                    optionText="name"
                    fullWidth
                    validate={[required()]}
                    variant="outlined"
                    disabled={isOccupied}
                  />
                </ReferenceInput>
              </Grid>
              
              {/* Tercera fila: Locker Type y Locker Controller */}
              <Grid item xs={12} md={6}>
                <ReferenceInput
                  source="lockerTypeId"
                  reference="locker-types"
                  label={translate('resources.lockers.fields.lockerType')}
                  filter={{ hotelId: hotelId }} // ✅ Usar el hotelId correcto
                  sort={{ field: 'name', order: 'ASC' }}
                >
                  <SelectInput 
                    optionText="name"
                    fullWidth
                    validate={[required()]}
                    variant="outlined"
                    disabled={isOccupied}
                  />
                </ReferenceInput>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <ReferenceInput
                  source="lockerControllerId"
                  reference="locker-controllers"
                  label={translate('resources.lockers.fields.lockerController')}
                  filter={{ hotelId: hotelId }} // ✅ Usar el hotelId correcto
                  sort={{ field: 'name', order: 'ASC' }}
                  allowEmpty
                >
                  <SelectInput 
                    optionText="name"
                    fullWidth
                    variant="outlined"
                    disabled={isOccupied}
                  />
                </ReferenceInput>
              </Grid>

              {/* Cuarta fila: Notes (ancho completo) */}
              <Grid item xs={12}>
                <TextInput
                  source="notes"
                  label={translate('resources.lockers.fields.notes')}
                  multiline
                  rows={4}
                  fullWidth
                  variant="outlined"
                  helperText={translate('resources.lockers.help.notes')}
                />
              </Grid>
              
              {/* Información adicional de solo lectura */}
              <Grid item xs={12}>
                <Box sx={{ mt: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    {translate('resources.lockers.sections.info')}
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" color="text.secondary">
                        {translate('resources.lockers.fields.hotel')}: {originalRecord?.hotel?.name}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" color="text.secondary">
                        {translate('resources.lockers.fields.createdAt')}: {new Date(record.createdAt).toLocaleDateString()}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
            </Grid>
          </MyFormSection>
          
        </CenteredFormLayout>
      </SimpleForm>
    </MyFormContainer>
  );
};

// Delete Dialog Component
const LockerDeleteDialog: React.FC<{
  open: boolean;
  onClose: () => void;
}> = ({ open, onClose }) => {
  const record = useRecordContext<Locker>();
  const translate = useTranslate();

  if (!record) return null;

  const deleteMessage = translate('resources.lockers.dialogs.delete_message', { 
    displayNumber: record.displayNumber 
  });

  return (
    <BaseDeleteDialog
      open={open}
      onClose={onClose}
      resource="lockers"
      recordId={record.id}
      title={translate('resources.lockers.dialogs.delete_title')}
      message={deleteMessage}
      warningMessage={
        translate(
          'resources.lockers.dialogs.delete_warning',
          'Esta acción no se puede deshacer. La taquilla quedará marcada como inactiva.'
        )
      }
    />
  );
};

export default LockerEdit;